import React from "react";
import {
  X,
  RotateCcw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Check,
} from "lucide-react";
import { formatTimestampFull } from "../services/api";
import { useBackdropClose } from "../utils/backdropClose";

// Preview of a saved version (snapshot). Renders a human-readable breakdown
// (never raw JSON); each section can be restored on its own, or the whole
// version can replace the current data.

const SECTION_LABELS = {
  tabs: "tabs",
  goals: "goals",
  subGoals: "sub-goals",
  clipboardCards: "clipboard cards",
  quickLinkCards: "quick link cards",
  rememberItems: "remember items",
  conferenceCards: "conferences",
  publications: "publications",
  stickyNotes: "sticky notes",
  days: "days",
};

const Chip = ({ label, value }) => (
  <span className="px-2 py-1 rounded-lg bg-gray-700/60 text-xs text-cyan-200">
    {label}: <span className="font-semibold text-white">{value}</span>
  </span>
);

const Section = ({ title, actions, children }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
      <div className="text-xs uppercase tracking-wider text-gray-500">
        {title}
      </div>
      {actions && (
        <div className="flex items-center gap-2 flex-wrap">{actions}</div>
      )}
    </div>
    {children}
  </div>
);

const SnapshotViewerModal = ({
  isOpen,
  snapshot,
  loading,
  error,
  onClose,
  onRestore,
  onRestoreSection,
  restoring,
}) => {
  // Full restore: 0 = idle, 1 = first confirmation, 2 = final confirmation.
  const [confirmStep, setConfirmStep] = React.useState(0);
  const [pendingKey, setPendingKey] = React.useState(null);
  const [busyKey, setBusyKey] = React.useState(null);
  const [doneKeys, setDoneKeys] = React.useState({});
  const [sectionMsg, setSectionMsg] = React.useState(null);
  const backdrop = useBackdropClose(onClose, !restoring && !busyKey);

  React.useEffect(() => {
    setConfirmStep(0);
    setPendingKey(null);
    setBusyKey(null);
    setDoneKeys({});
    setSectionMsg(null);
  }, [isOpen, snapshot]);

  if (!isOpen) return null;

  const data = snapshot && snapshot.data ? snapshot.data : null;
  const tabs = Array.isArray(data?.tabs) ? data.tabs : [];
  const subGoals = Array.isArray(data?.subGoals) ? data.subGoals : [];
  const clipboard = Array.isArray(data?.clipboardCards)
    ? data.clipboardCards
    : [];
  const quickLinks = Array.isArray(data?.quickLinkCards)
    ? data.quickLinkCards
    : [];
  const remember = Array.isArray(data?.rememberItems) ? data.rememberItems : [];
  const conferences = Array.isArray(data?.conferenceCards)
    ? data.conferenceCards
    : [];
  const publications = Array.isArray(data?.publications)
    ? data.publications
    : [];
  const stickyNotes = Array.isArray(data?.stickyNotes) ? data.stickyNotes : [];
  const completed = Array.isArray(data?.completedDays)
    ? data.completedDays.length
    : 0;
  const partial = Array.isArray(data?.partialDays)
    ? data.partialDays.length
    : 0;
  const s = data?.settings || {};
  const goalCount = tabs.reduce(
    (n, t) => n + (Array.isArray(t.goals) ? t.goals.length : 0),
    0,
  );
  const goalText = new Map();
  tabs.forEach((t) =>
    (t.goals || []).forEach((g) => goalText.set(String(g.id), g.text)),
  );

  const runSectionRestore = async (key) => {
    setPendingKey(null);
    setBusyKey(key);
    setSectionMsg(null);
    try {
      const r = await onRestoreSection(key);
      const n = r && typeof r.restored === "number" ? r.restored : 0;
      setDoneKeys((prev) => ({ ...prev, [key]: true }));
      setSectionMsg({
        ok: true,
        text: `Restored ${n} ${SECTION_LABELS[key]} from this version.`,
      });
    } catch (err) {
      setSectionMsg({
        ok: false,
        text: err.message || `Could not restore ${SECTION_LABELS[key]}.`,
      });
    } finally {
      setBusyKey(null);
    }
  };

  const restoreAction = (key, count) => {
    if (!onRestoreSection || count === 0) return null;
    if (busyKey === key) {
      return (
        <span key={key} className="text-xs text-cyan-300">
          Restoring {SECTION_LABELS[key]}…
        </span>
      );
    }
    if (pendingKey === key) {
      return (
        <span key={key} className="flex items-center gap-1.5">
          <span className="text-[11px] text-yellow-300">
            Restore {count} {SECTION_LABELS[key]}?
          </span>
          <button
            onClick={() => runSectionRestore(key)}
            className="px-2 py-1 bg-yellow-500 text-black rounded-lg text-xs font-medium"
          >
            Yes
          </button>
          <button
            onClick={() => setPendingKey(null)}
            className="px-2 py-1 bg-gray-800 text-gray-300 rounded-lg text-xs"
          >
            Cancel
          </button>
        </span>
      );
    }
    return (
      <button
        key={key}
        disabled={!!busyKey || restoring}
        onClick={() => {
          setSectionMsg(null);
          setConfirmStep(0);
          setPendingKey(key);
        }}
        className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-800 text-cyan-300 border border-cyan-500/30 hover:border-cyan-500 flex items-center gap-1 disabled:opacity-50"
        title={`Bring back this version's ${SECTION_LABELS[key]} (items added since are kept)`}
      >
        {doneKeys[key] ? (
          <Check className="w-3 h-3 text-green-400" />
        ) : (
          <RotateCcw className="w-3 h-3" />
        )}
        Restore {SECTION_LABELS[key]}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      {...backdrop}
    >
      <div className="bg-gray-900 rounded-3xl border border-gray-700 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-700 flex items-start justify-between gap-3 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white">Version preview</h3>
            {snapshot && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" />
                {formatTimestampFull(snapshot.takenAt)}
                {snapshot.reason ? ` · ${snapshot.reason}` : ""}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <p className="text-cyan-300">Loading version…</p>}
          {error && <p className="text-red-400">{error}</p>}

          {!loading && !error && data && (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                <Chip label="Tabs" value={tabs.length} />
                <Chip label="Goals" value={goalCount} />
                <Chip label="Sub-goals" value={subGoals.length} />
                <Chip label="Clipboard" value={clipboard.length} />
                <Chip label="Quick links" value={quickLinks.length} />
                <Chip label="Remember" value={remember.length} />
                <Chip label="Conferences" value={conferences.length} />
                <Chip label="Publications" value={publications.length} />
                <Chip label="Sticky notes" value={stickyNotes.length} />
                <Chip
                  label="Days"
                  value={`${completed} done / ${partial} partial`}
                />
              </div>

              {onRestoreSection && (
                <p className="text-xs text-gray-400 mb-4">
                  Restoring a single section brings back that section's items
                  as they were in this version and keeps anything you added
                  since. Everything else stays as it is now.
                </p>
              )}

              <Section title="Settings">
                <div className="text-sm text-cyan-100">
                  {s.challengeName || "My Challenge"} · {s.numberOfDays || 0}{" "}
                  days · starts {s.startDate || "—"}
                </div>
              </Section>

              {(completed > 0 || partial > 0) && (
                <Section
                  title="Days"
                  actions={restoreAction("days", completed + partial)}
                >
                  <div className="text-sm text-cyan-100">
                    {completed} completed · {partial} partial
                  </div>
                </Section>
              )}

              {tabs.length > 0 && (
                <Section
                  title="Tabs & Goals"
                  actions={[
                    restoreAction("tabs", tabs.length),
                    restoreAction("goals", goalCount),
                  ]}
                >
                  <div className="space-y-2">
                    {tabs.map((t) => (
                      <div
                        key={t.id}
                        className="rounded-xl border border-gray-700/70 p-2"
                      >
                        <div className="text-sm font-medium text-cyan-300 mb-1">
                          {t.name || "Untitled tab"}{" "}
                          <span className="text-gray-500">
                            ({(t.goals || []).length})
                          </span>
                        </div>
                        {(t.goals || []).length > 0 && (
                          <ul className="space-y-0.5">
                            {(t.goals || []).map((g) => (
                              <li
                                key={g.id}
                                className="flex items-center gap-2 text-sm text-cyan-100"
                              >
                                {g.completed ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                ) : (
                                  <Circle className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                )}
                                <span
                                  className={
                                    g.completed
                                      ? "line-through text-gray-400"
                                      : ""
                                  }
                                >
                                  {g.text || "—"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {subGoals.length > 0 && (
                <Section
                  title="Sub-goals"
                  actions={restoreAction("subGoals", subGoals.length)}
                >
                  <ul className="space-y-0.5">
                    {subGoals.map((sg) => (
                      <li
                        key={sg.id}
                        className="flex items-center gap-2 text-sm text-cyan-100"
                      >
                        {sg.completed ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                        )}
                        <span
                          className={
                            sg.completed ? "line-through text-gray-400" : ""
                          }
                        >
                          {sg.text || "—"}
                        </span>
                        {goalText.has(String(sg.goalId)) && (
                          <span className="text-gray-500 text-xs truncate">
                            · {goalText.get(String(sg.goalId))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {conferences.length > 0 && (
                <Section
                  title="Conferences"
                  actions={restoreAction("conferenceCards", conferences.length)}
                >
                  <ul className="space-y-0.5">
                    {conferences.map((c) => (
                      <li key={c.id} className="text-sm text-cyan-100">
                        • {c.title || "Untitled"}{" "}
                        <span className="text-gray-500">
                          ({(c.entries || []).length} edition
                          {(c.entries || []).length === 1 ? "" : "s"})
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {publications.length > 0 && (
                <Section
                  title="Publications"
                  actions={restoreAction("publications", publications.length)}
                >
                  <ul className="space-y-0.5">
                    {publications.map((p) => (
                      <li key={p.id} className="text-sm text-cyan-100">
                        • {p.title || p.shortName || "Untitled"}{" "}
                        <span className="text-gray-500">
                          ({p.status || "drafting"}
                          {(p.history || []).length > 0
                            ? `, ${(p.history || []).length} past attempt${
                                (p.history || []).length === 1 ? "" : "s"
                              }`
                            : ""}
                          )
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {quickLinks.length > 0 && (
                <Section
                  title="Quick links"
                  actions={restoreAction("quickLinkCards", quickLinks.length)}
                >
                  <ul className="space-y-0.5">
                    {quickLinks.map((c) => (
                      <li key={c.id} className="text-sm text-cyan-100">
                        • {c.title || "Untitled"}{" "}
                        <span className="text-gray-500">
                          ({(c.links || []).length} link
                          {(c.links || []).length === 1 ? "" : "s"})
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {clipboard.length > 0 && (
                <Section
                  title="Clipboard"
                  actions={restoreAction("clipboardCards", clipboard.length)}
                >
                  <ul className="space-y-0.5">
                    {clipboard.map((c) => (
                      <li key={c.id} className="text-sm text-cyan-100">
                        • {c.title || "Untitled"}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {remember.length > 0 && (
                <Section
                  title="Remember"
                  actions={restoreAction("rememberItems", remember.length)}
                >
                  <ul className="space-y-0.5">
                    {remember.map((r) => (
                      <li key={r.id} className="text-sm text-cyan-100">
                        • {r.text || "—"}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {stickyNotes.length > 0 && (
                <Section
                  title="Sticky notes"
                  actions={restoreAction("stickyNotes", stickyNotes.length)}
                >
                  <ul className="space-y-0.5">
                    {stickyNotes.map((n) => (
                      <li key={n.id} className="text-sm text-cyan-100 truncate">
                        • {(n.text || "").split("\n")[0] || "Empty note"}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}

          {!loading && !error && !data && (
            <p className="text-gray-400">This version has no readable data.</p>
          )}
        </div>

        {sectionMsg && (
          <div
            className={`mx-5 mb-3 p-2.5 rounded-xl border text-sm flex-shrink-0 ${
              sectionMsg.ok
                ? "bg-green-600/10 border-green-500/30 text-green-300"
                : "bg-red-600/10 border-red-500/30 text-red-300"
            }`}
          >
            {sectionMsg.text}
          </div>
        )}

        {/* Footer */}
        <div className="p-5 border-t border-gray-700 flex items-center justify-between gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors text-sm"
          >
            Close
          </button>
          {data &&
            (confirmStep > 0 ? (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <span className="text-xs text-yellow-300 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {confirmStep === 1
                    ? "Replace ALL current data with this version? (1 of 2)"
                    : "Final check: everything changed since this version will be overwritten. (2 of 2)"}
                </span>
                <button
                  disabled={restoring}
                  onClick={() =>
                    confirmStep === 1
                      ? setConfirmStep(2)
                      : onRestore && onRestore()
                  }
                  className="px-3 py-2 bg-yellow-500 text-black rounded-xl text-sm font-medium disabled:opacity-50"
                >
                  {restoring
                    ? "Restoring…"
                    : confirmStep === 1
                      ? "Continue"
                      : "Yes, restore everything"}
                </button>
                <button
                  disabled={restoring}
                  onClick={() => setConfirmStep(0)}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-xl text-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                disabled={!!busyKey}
                onClick={() => {
                  setPendingKey(null);
                  setConfirmStep(1);
                }}
                className="px-4 py-2 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                Restore whole version
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default SnapshotViewerModal;
